/**
 * src/components/spatial-bindings/bindingUi.tsx
 * Styles and small presentational pieces shared by the binding surfaces.
 *
 * Self-contained inline styles on purpose. These components render inside
 * two hosts with incompatible conventions — OrganizationsSection uses local
 * inline style objects, SpatialObjectsAdminOverlay uses a `.spatial-admin-*`
 * stylesheet — so importing either one's vocabulary would make the panel
 * look correct in one place and wrong in the other. The values match
 * OrganizationsSection, which is the denser of the two.
 */

import React from 'react';
import type { BindingStatusName, ObjectStatusName } from '../../v2/services/spatialRegistryService';
import type { ReconciliationState } from '../../v2/services/spatialBindingModel';

export const hint: React.CSSProperties = { fontSize: '12px', color: '#666' };

export const input: React.CSSProperties = {
  padding: '6px', fontSize: '12px', border: '1px solid #ddd',
  borderRadius: '4px', boxSizing: 'border-box',
};

export const button = (
  enabled: boolean,
  tone: 'primary' | 'danger' | 'plain' | 'warn' = 'primary',
): React.CSSProperties => ({
  padding: '6px 14px',
  backgroundColor: !enabled
    ? '#ccc'
    : tone === 'danger' ? '#dc3545'
    : tone === 'plain' ? '#6c757d'
    : tone === 'warn' ? '#c77700'
    : '#007bff',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  cursor: enabled ? 'pointer' : 'not-allowed',
  fontSize: '12px',
  fontWeight: 500,
});

export const sectionTitle: React.CSSProperties = {
  fontSize: '12px', fontWeight: 600, marginBottom: '6px',
};

export const mono: React.CSSProperties = { fontFamily: 'monospace' };

// ── Pills ────────────────────────────────────────────────────────────────────

const TONE = {
  neutral: { bg: '#e9ecef', fg: '#495057' },
  good:    { bg: '#d4edda', fg: '#155724' },
  warn:    { bg: '#fff3cd', fg: '#856404' },
  bad:     { bg: '#f8d7da', fg: '#721c24' },
  info:    { bg: '#d9e9ff', fg: '#0b4a8f' },
} as const;

export type PillTone = keyof typeof TONE;

export const Pill: React.FC<{ tone?: PillTone; title?: string; children: React.ReactNode }> = ({
  tone = 'neutral', title, children,
}) => {
  const t = TONE[tone];
  return (
    <span
      title={title}
      style={{
        padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600,
        backgroundColor: t.bg, color: t.fg, whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
};

const BINDING_TONE: Record<BindingStatusName, PillTone> = {
  none: 'neutral', proposed: 'warn', approved: 'good', suspended: 'warn', removed: 'bad',
};

export const BindingStatusPill: React.FC<{ status: BindingStatusName; label: string }> = ({
  status, label,
}) => <Pill tone={BINDING_TONE[status] ?? 'neutral'}>{label}</Pill>;

const OBJECT_TONE: Record<ObjectStatusName, PillTone> = {
  none: 'neutral', active: 'good', suspended: 'warn', retired: 'bad',
};

export const ObjectStatusPill: React.FC<{ status: ObjectStatusName; label: string }> = ({
  status, label,
}) => <Pill tone={OBJECT_TONE[status] ?? 'neutral'}>{label}</Pill>;

// ── Capability readout ───────────────────────────────────────────────────────

/**
 * The three capabilities, always all three.
 *
 * Shown together and never collapsed to a single "verified" badge: they are
 * independent bars, and one of them being true says nothing about the other
 * two. An organization that can own an object but cannot operate it is a
 * normal, correct state that the operator has to be able to see.
 */
export const CapabilityList: React.FC<{
  canOwn: boolean;
  canOperate: boolean;
  canReceive: boolean;
  compact?: boolean;
}> = ({ canOwn, canOperate, canReceive, compact = false }) => {
  const items: [string, boolean][] = [
    ['Can own objects', canOwn],
    ['Can operate objects', canOperate],
    ['Can receive funds', canReceive],
  ];
  return (
    <div style={{ display: 'flex', gap: compact ? '6px' : '10px', flexWrap: 'wrap' }}>
      {items.map(([label, ok]) => (
        <Pill key={label} tone={ok ? 'good' : 'neutral'}>
          {ok ? '✓' : '✕'} {label}
        </Pill>
      ))}
    </div>
  );
};

// ── Banners ──────────────────────────────────────────────────────────────────

export const Banner: React.FC<{
  tone: 'error' | 'warn' | 'ok' | 'info';
  children: React.ReactNode;
}> = ({ tone, children }) => {
  const palette = {
    error: { bg: '#f8d7da', fg: '#721c24', border: '#f5c6cb' },
    warn:  { bg: '#fff3cd', fg: '#856404', border: '#ffeeba' },
    ok:    { bg: '#d4edda', fg: '#155724', border: '#c3e6cb' },
    info:  { bg: '#e7f1ff', fg: '#0b4a8f', border: '#bcd8ff' },
  }[tone];
  return (
    <div style={{
      padding: '8px 10px', borderRadius: '4px', fontSize: '12px', marginBottom: '8px',
      backgroundColor: palette.bg, color: palette.fg, border: `1px solid ${palette.border}`,
    }}>
      {children}
    </div>
  );
};

const RECONCILIATION_TONE: Record<ReconciliationState, 'warn' | 'info' | null> = {
  in_sync: null,
  unbound: null,
  chain_only: 'info',
  geolink_only: 'warn',
  divergent: 'warn',
};

export const ReconciliationNotice: React.FC<{
  state: ReconciliationState;
  message: string;
}> = ({ state, message }) => {
  const tone = RECONCILIATION_TONE[state];
  if (!tone || !message) return null;
  return <Banner tone={tone}>{message}</Banner>;
};

// ── Modal shell ──────────────────────────────────────────────────────────────

export const ModalShell: React.FC<{
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
}> = ({ title, onClose, children, footer }) => (
  <div
    onClick={onClose}
    style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 12000,
      padding: '16px',
    }}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        backgroundColor: '#fff', borderRadius: '8px', width: 'min(560px, 100%)',
        maxHeight: '86vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 8px 30px rgba(0,0,0,0.25)',
      }}
    >
      <div style={{
        padding: '12px 16px', borderBottom: '1px solid #eee',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ fontSize: '14px', fontWeight: 600 }}>{title}</div>
        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            border: 'none', background: 'transparent', fontSize: '18px',
            cursor: 'pointer', color: '#888', lineHeight: 1,
          }}
        >
          ×
        </button>
      </div>
      <div style={{ padding: '14px 16px', overflowY: 'auto', flex: 1 }}>{children}</div>
      {footer && (
        <div style={{
          padding: '10px 16px', borderTop: '1px solid #eee',
          display: 'flex', gap: '8px', justifyContent: 'flex-end',
        }}>
          {footer}
        </div>
      )}
    </div>
  </div>
);
